import type { ReactNode } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { amount } from '../../../lib/money';
import { STANDARD_CONDITIONS } from './standardConditions';
import { chargeableWeight, chargeTotal, totalPrice, type QuoteForm } from './quoteForm';

const line = '1px solid #222';
const cell = { border: line, p: '4px 8px', fontSize: 12, textAlign: 'center' as const, color: '#111' };
const head = { ...cell, bgcolor: '#d9d9d9', fontWeight: 700 };

/** 2026-01-11 → "11 - 01 - 2026", the way the template writes dates */
const day = (iso: string) => (iso ? iso.split('-').reverse().join(' - ') : '');

/** A bordered table; the rows listed in `heads` are grey header rows */
function Grid({ rows, heads = [0] }: { rows: ReactNode[][]; heads?: number[] }) {
  return (
    <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, k) => (
              <Box component="td" key={k} sx={heads.includes(i) ? head : cell}>
                {c}
              </Box>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  );
}

/** A picture of the quotation, drawn from what is typed. The real PDF comes from the SERB Word template. */
export function QuotePreview({ form }: { form: QuoteForm }) {
  const weight = chargeableWeight(form);
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        color: '#111',
        p: { xs: 2, sm: 4 },
        boxShadow: 2,
        fontFamily: 'Arial, sans-serif',
        overflowX: 'auto',
      }}
    >
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box component="img" src="/icons/logo-full.png" alt="SERB" sx={{ height: 52 }} />
        <Typography sx={{ fontSize: 28, fontWeight: 800 }}>Quotation</Typography>
        <Typography sx={{ fontSize: 12, textAlign: 'right' }}>
          195, Madina Monawara Str.
          <br />
          Amman, Jordan
        </Typography>
      </Stack>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', mb: 2 }}>
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {[
              ['Quotation Number', form.number],
              ['Quotation Date', day(form.date)],
              ['Quote Validity', day(form.validity)],
            ].map(([k, v]) => (
              <tr key={k}>
                <Box component="td" sx={head}>
                  {k}
                </Box>
                <Box component="td" sx={cell}>
                  {v}
                </Box>
              </tr>
            ))}
          </tbody>
        </table>
        <Stack>
          <Box sx={head}>Customer Name</Box>
          <Box sx={{ ...cell, fontWeight: 700 }}>{form.customer || ' '}</Box>
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', mb: 2 }}>
        <Grid
          rows={[
            ['Origin', 'Destination', 'Transit Time'],
            [form.origin, form.destination, form.transit],
            ['Cut Off Date', 'Sailing Date', ''],
            [day(form.cutOff), day(form.sailing), ''],
          ]}
          heads={[0, 2]}
        />
        <Grid
          rows={[
            ['Terms', 'Carrier'],
            [form.incoterm, form.carrier],
          ]}
        />
      </Box>

      <Box sx={{ ...head, mt: 1 }}>Shipment Information</Box>
      <Grid
        rows={[
          ['Description', 'Containers', 'Dimensions', 'Chargeable Weight', 'Gross Weight'],
          ...form.cargo.map((c) => [
            c.description,
            c.containers || '–',
            c.dimensions || '–',
            c.chargeable && `${c.chargeable} kg`,
            c.gross && `${c.gross} kg`,
          ]),
        ]}
      />

      <Box sx={{ ...head, mt: 2 }}>Charges Description</Box>
      <Grid
        rows={[
          ['Item', 'Units', 'Unit Price', 'Total Price'],
          ...form.charges
            .filter((c) => c.item)
            .map((c) => [
              c.item,
              c.units === 'per kg' && weight ? 'per kg' : c.units,
              `USD ${amount(Number(c.unitPrice) || 0)}`,
              `USD ${amount(chargeTotal(c, form))}`,
            ]),
        ]}
      />
      <Stack direction="row" sx={{ border: line, mt: '-1px' }}>
        <Box sx={{ ...head, flex: 3, border: 0 }}>Total Price :</Box>
        <Box sx={{ ...head, flex: 1, border: 0, borderLeft: line }}>USD {amount(totalPrice(form))}</Box>
      </Stack>

      <Typography sx={{ mt: 2, fontSize: 11, fontWeight: 700 }}>General Conditions</Typography>
      <Box component="ol" sx={{ m: 0, pl: 2, fontSize: 11 }}>
        {STANDARD_CONDITIONS.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </Box>
      {form.conditions && (
        <Typography sx={{ mt: 0.5, fontSize: 11, whiteSpace: 'pre-line' }}>{form.conditions}</Typography>
      )}
    </Box>
  );
}
